'use client'
import { parseAsArrayOf, parseAsString, useQueryStates } from 'nuqs'
import { useCallback } from 'react'

type ReturnType = {
  createQueryString: (key: string[], value: string[], existingParams?: string) => string
  removeQueryString: (key: string[], value: string[]) => string
  removeQueryStringByKey: (keys: string[]) => string
  useCreateNewPathname: () => (newPathname: string) => string
  updateQuery: (updates: Record<string, string | string[] | null>) => Promise<URLSearchParams>
  getQueryValue: (key: string) => string | null
  getQueryValues: (key: string) => string[]
}

// Danh sách các key được xem như single value (sẽ replace thay vì append)
const SINGLE_VALUE_KEYS = ['sort', 'search', 'page', 'sortBy', 'price']

export function useChangeQuery(): ReturnType {
  // Sử dụng nuqs để quản lý query states
  const [queryStates, setQueryStates] = useQueryStates({
    sort: parseAsString,
    search: parseAsString,
    page: parseAsString,
    sortBy: parseAsString,
    price: parseAsString,
    // Các filter khác có thể là array
    category: parseAsArrayOf(parseAsString, ','),
    brand: parseAsArrayOf(parseAsString, ','),
    color: parseAsArrayOf(parseAsString, ','),
    size: parseAsArrayOf(parseAsString, ','),
    // Các filter khác nếu có thể thêm vào đây
    // Ví dụ: material, rating, availability, v.v.
  })

  const createQueryString = useCallback((key: string[], value: string[], existingParams?: string) => {
    const params = new URLSearchParams(existingParams ?? window.location.search)

    value.forEach((val, index) => {
      const currentKey = key[index]
      const existingValue = params.get(currentKey)

      if (SINGLE_VALUE_KEYS.includes(currentKey)) {
        params.set(currentKey, val)
      } else if (existingValue) {
        const valuesArray = existingValue?.split(',') ?? []
        if (!valuesArray.includes(val)) {
          params.set(currentKey, `${existingValue},${val}`)
        }
      } else {
        params.set(currentKey, val)
      }
    })

    return params.toString()
  }, [])

  const removeQueryString = useCallback((key: string[], value: string[]) => {
    const params = new URLSearchParams(window.location.search)

    value.forEach((val: string, index: number) => {
      const currentKey = key[index]
      const existingValue = params.get(currentKey)

      if (existingValue) {
        const valuesArray = existingValue.split(',')
        const updatedValuesArray = valuesArray.filter((item) => item !== val)

        if (updatedValuesArray.length > 0) {
          params.set(currentKey, updatedValuesArray.join(','))
        } else {
          params.delete(currentKey)
        }
      }
    })
    return params.toString()
  }, [])

  const removeQueryStringByKey = useCallback((keys: string[]) => {
    const params = new URLSearchParams(window.location.search)

    keys.forEach((key) => {
      params.delete(key)
    })

    return params.toString()
  }, [])

  const useCreateNewPathname = () => {
    const createNewPathname = useCallback((newPathname: string) => {
      const params = new URLSearchParams(window.location.search)
      const newUrl = `${newPathname}?${params.toString()}`
      return newUrl
    }, [])

    return createNewPathname
  }

  // Hàm mới sử dụng nuqs để update query params
  const updateQuery = useCallback(
    async (updates: Record<string, string | string[] | null>) => {
      const newStates: any = {}

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
          newStates[key] = null // Xóa param
        } else if (Array.isArray(value)) {
          newStates[key] = value.length > 0 ? value : null
        } else {
          newStates[key] = value
        }
      })

      await setQueryStates(newStates)
      return new URLSearchParams(window.location.search)
    },
    [setQueryStates]
  )

  // Hàm tiện ích để lấy giá trị query (chỉ trả về string hoặc null)
  const getQueryValue = useCallback(
    (key: string): string | null => {
      const value = queryStates[key as keyof typeof queryStates]
      if (Array.isArray(value)) {
        return value.length > 0 ? value[0] : null
      }
      return value || null
    },
    [queryStates]
  )

  // Hàm tiện ích để lấy giá trị query dạng array
  const getQueryValues = useCallback(
    (key: string) => {
      const value = queryStates[key as keyof typeof queryStates]
      if (Array.isArray(value)) {
        return value
      }
      return value ? [value] : []
    },
    [queryStates]
  )

  return {
    createQueryString,
    removeQueryString,
    removeQueryStringByKey,
    useCreateNewPathname,
    updateQuery,
    getQueryValue,
    getQueryValues,
  }
}
